// src/pages/championships/ManageGroups.jsx
// Versão Web completa sincronizada com Mobile React Native
// ✅ Gerador de mata-mata com busca automática de classificados, posição e grupo de origem
// ✅ Auto-preenchimento de local padrão (venue/location)
// ✅ CORRIGIDO: Suporte a targetPhase para buscar perdedores (3º lugar)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import championshipService from '../../services/championshipService';
import matchService from '../../services/matchService';
import { getGroupPreview } from '../../services/standingsApiService';
import KnockoutPairingTool from '../../pages/championships/KnockoutPairingTool';
import useAuth from '../../hooks/useAuth';
import './ManageGroups.css';

// Mapeamento de fase para fase anterior
const phasePreviousPhaseMap = {
    'roundOf32': 'initial',
    'roundOf16': 'initial',
    'quarterFinals': 'roundOf16',
    'semiFinals': 'quarterFinals',
    'final': 'semiFinals',
    'thirdPlace': 'semiFinals', // ✅ 3º lugar usa semifinais como fase anterior
};

// ✅ FUNÇÃO HELPER PARA FORMATAR DATA E HORA
const formatDateTime = (dateString) => {
    if (!dateString) return 'Data a definir';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return date.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

// Função auxiliar para traduzir fases de mata-mata para o português
const translatePhaseToPortuguese = (phase) => {
    const phases = {
        'roundOf16': 'Oitavas de Final',
        'quarterFinals': 'Quartas de Final',
        'semiFinals': 'Semifinal',
        'thirdPlace': 'Disputa de 3º Lugar',
        'final': 'Final',
        'group': 'Fase de Grupos',
        'roundOf32': '32ªs de Final'
    };
    return phases[phase] || phase;
};

// ✅ COMPONENTE: Gerador de Fase do Mata-Mata para Web (CORRIGIDO com targetPhase)
const KnockoutPhaseGeneratorWeb = ({ 
    phase, 
    phaseTitle, 
    championshipId, 
    onGenerate, 
    isGenerating, 
    existingMatches, 
    defaultLocation = '' 
}) => {
    const [availableTeams, setAvailableTeams] = useState([]);
    const [loadingQualifiers, setLoadingQualifiers] = useState(true);
    const [qualifierError, setQualifierError] = useState('');

    const [selectedTeams, setSelectedTeams] = useState([]);
    const [pairings, setPairings] = useState([]);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState('15:00');
    const [location, setLocation] = useState(defaultLocation);

    // 🟢 Sincroniza o local padrão quando recebido do campeonato
    useEffect(() => {
        if (defaultLocation) {
            setLocation(defaultLocation);
        }
    }, [defaultLocation]);

    // 🟢 Busca automática dos times classificados para a fase selecionada
    // ✅ CORRIGIDO: Passa targetPhase para buscar perdedores quando for thirdPlace
    useEffect(() => {
        const fetchQualifiersForPhase = async () => {
            setLoadingQualifiers(true);
            setQualifierError('');
            try {
                const previousPhaseKey = phasePreviousPhaseMap[phase] || 'initial';
                
                // ✅ Define targetPhase para 3º lugar
                let targetPhase = '';
                if (phase === 'thirdPlace') {
                    targetPhase = 'thirdPlace';
                    console.log(`🔴 Buscando PERDEDORES para 3º lugar (fase anterior: ${previousPhaseKey})`);
                } else {
                    console.log(`🟢 Buscando VENCEDORES para ${phaseTitle} (fase anterior: ${previousPhaseKey})`);
                }
                
                const res = await championshipService.getKnockoutQualifiers(championshipId, previousPhaseKey, targetPhase);
                const teamsData = res.data?.data || res.data || [];
                setAvailableTeams(teamsData);
                console.log(`📊 ${teamsData.length} times retornados para fase ${phase}`);
            } catch (err) {
                console.error('Erro ao buscar classificados:', err);
                const msg = err.response?.data?.error || err.message || 'Não foi possível carregar os times classificados.';
                if (msg.includes('fase de grupos precisa ser')) {
                    setQualifierError('A fase de grupos ainda não foi finalizada. Conclua todas as partidas pendentes para obter os classificados.');
                } else {
                    setQualifierError(msg);
                }
            } finally {
                setLoadingQualifiers(false);
            }
        };

        if (championshipId && phase) {
            fetchQualifiersForPhase();
        }
    }, [championshipId, phase, phaseTitle]);

    const requiredMatches = useMemo(() => {
        switch (phase) {
            case 'roundOf32': return 16;
            case 'roundOf16': return 8;
            case 'quarterFinals': return 4;
            case 'semiFinals': return 2;
            case 'final': return 1;
            case 'thirdPlace': return 1;
            default: return 0;
        }
    }, [phase]);

    const toggleTeam = (teamId) => {
        setSelectedTeams(prev => {
            if (prev.includes(teamId)) return prev.filter(id => id !== teamId);
            if (prev.length >= 2) { alert('Selecione no máximo 2 times por confronto.'); return prev; }
            return [...prev, teamId];
        });
    };

    const handleAddPairing = () => {
        if (selectedTeams.length !== 2) { alert('Selecione exatamente 2 times.'); return; }
        const teamA = availableTeams.find(t => t._id === selectedTeams[0]);
        const teamB = availableTeams.find(t => t._id === selectedTeams[1]);
        if (teamA._id === teamB._id) { alert('Os times devem ser diferentes.'); return; }
        setPairings(prev => [...prev, { teamA, teamB }]);
        setSelectedTeams([]);
    };

    const handleGenerate = () => {
        if (pairings.length !== requiredMatches) { alert(`São necessários ${requiredMatches} confrontos.`); return; }
        if (!location.trim()) { alert('Defina o local das partidas.'); return; }
        const finalDate = new Date(`${date}T${time}`);
        onGenerate(phase, pairings, { date: finalDate.toISOString(), location: location.trim() });
    };

    // Times que ainda não foram pareados
    const unselectedQualifiers = availableTeams.filter(
        t => !pairings.some(p => p.teamA._id === t._id || p.teamB._id === t._id)
    );

    // Determina se é 3º lugar para exibir aviso
    const isThirdPlace = phase === 'thirdPlace';

    return (
        <div className="phase-generator-body">
            {isThirdPlace && (
                <div style={{ backgroundColor: '#ff6b6b20', padding: '8px', borderRadius: '5px', marginBottom: '12px', border: '1px solid #ff6b6b' }}>
                    <p style={{ color: '#ff6b6b', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>
                        ⚠️ Serão usados os PERDEDORES das semifinais
                    </p>
                </div>
            )}
            {existingMatches && existingMatches.length > 0 && (
                <p className="phase-generator-warning">⚠️ Esta fase já possui partidas. Gerar novamente substituirá as existentes.</p>
            )}
            {qualifierError && (
                <p className="phase-generator-warning" style={{ color: '#dc3545', backgroundColor: '#f8d7da', borderColor: '#f5c6cb' }}>
                    ⚠️ {qualifierError}
                </p>
            )}
            <div className="phase-generator-schedule">
                <label>Data e Hora Padrão</label>
                <div className="form-row">
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} />
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} />
                </div>
                <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Local das partidas" className="phase-generator-location" />
            </div>
            <div className="phase-generator-teams">
                <label>
                    Times Classificados ({unselectedQualifiers.length}/{availableTeams.length})
                    {isThirdPlace && <span style={{ color: '#ff6b6b', marginLeft: '8px' }}>(Perdedores das Semifinais)</span>}
                    — selecione 2 por confronto
                </label>
                {loadingQualifiers ? (
                    <p style={{ color: '#aaa', fontStyle: 'italic', padding: '10px 0' }}>Carregando classificados...</p>
                ) : unselectedQualifiers.length === 0 ? (
                    <p style={{ color: '#888', fontStyle: 'italic', padding: '10px 0' }}>
                        {availableTeams.length === 0 ? 'Nenhum time classificado encontrado.' : 'Todos os classificados já foram pareados abaixo.'}
                    </p>
                ) : (
                    <div className="phase-teams-scroll">
                        {unselectedQualifiers.map(team => {
                            const posLabel = team.position ? `${team.position}º` : '';
                            const groupLabel = team.groupNameNormalized ? `Grupo ${team.groupNameNormalized}` : (team.groupName || '');
                            const tagText = [posLabel, groupLabel].filter(Boolean).join(' - ');
                            const isPerdedor = team.position === 'Perdedor';

                            return (
                                <div
                                    key={team._id}
                                    className={`phase-team-chip${selectedTeams.includes(team._id) ? ' selected' : ''}`}
                                    onClick={() => toggleTeam(team._id)}
                                    style={isThirdPlace && isPerdedor ? { borderColor: '#ff6b6b', backgroundColor: '#2a1a1a' } : {}}
                                >
                                    {team.badge && <img src={team.badge} alt={team.name} />}
                                    <span>{team.name}</span>
                                    {tagText && (
                                        <small className="qualifier-badge-tag" style={{ display: 'block', fontSize: '10px', color: '#ffc107', marginTop: '2px' }}>
                                            {tagText}
                                        </small>
                                    )}
                                    {isThirdPlace && isPerdedor && (
                                        <span style={{ fontSize: '9px', color: '#ff6b6b', fontWeight: 'bold', display: 'block' }}>
                                            PERDEDOR
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <button className="generate-groups-btn" onClick={handleAddPairing} disabled={selectedTeams.length !== 2}>
                + Adicionar Confronto ({selectedTeams.length}/2)
            </button>
            {pairings.length > 0 && (
                <div className="pairings-list">
                    <p><strong>Confrontos Montados ({pairings.length}/{requiredMatches})</strong></p>
                    {pairings.map((pair, i) => (
                        <div key={i} className="pairing-item">
                            <span>
                                {pair.teamA.name} {pair.teamA.position ? `(${pair.teamA.position}º ${pair.teamA.groupNameNormalized ? `Grp ${pair.teamA.groupNameNormalized}` : ''})` : ''} 
                                {' '}vs{' '} 
                                {pair.teamB.name} {pair.teamB.position ? `(${pair.teamB.position}º ${pair.teamB.groupNameNormalized ? `Grp ${pair.teamB.groupNameNormalized}` : ''})` : ''}
                            </span>
                            <button onClick={() => setPairings(prev => prev.filter((_, idx) => idx !== i))} className="delete-btn">✕</button>
                        </div>
                    ))}
                </div>
            )}
            <button
                className="generate-matches-btn"
                onClick={handleGenerate}
                disabled={pairings.length !== requiredMatches || isGenerating}
                style={{ marginTop: '12px', width: '100%' }}
            >
                {isGenerating ? 'Gerando...' : `Gerar e Agendar ${phaseTitle}`}
            </button>
        </div>
    );
};

// ------------------------------------------------------------
// O restante do código (AddMatchForm, AddIntergroupMatchForm, ManageGroups principal)
// permanece igual ao original, apenas o KnockoutPhaseGeneratorWeb foi modificado.
// Para completude, incluo o código completo abaixo, mas as alterações essenciais estão acima.
// ------------------------------------------------------------

const AddMatchForm = ({ group, teams, onMatchAdded, championshipVenue }) => {
    const { id: championshipId } = useParams();
    const [teamA, setTeamA] = useState('');
    const [teamB, setTeamB] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState('15:00');
    const [round, setRound] = useState(1);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [locationOption, setLocationOption] = useState('default');
    const [manualLocation, setManualLocation] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!teamA || !teamB || teamA === teamB) {
            setError('Selecione dois times diferentes.');
            return;
        }

        let finalLocation = '';
        if (locationOption === 'default') {
            finalLocation = championshipVenue || 'A definir';
        } else {
            finalLocation = manualLocation.trim();
        }

        if (!finalLocation) {
            setError('Por favor, defina o local da partida.');
            return;
        }

        setIsSubmitting(true);
        try {
            const matchDateTime = new Date(`${date}T${time}`);
            const matchData = { 
                teamA, 
                teamB, 
                date: matchDateTime.toISOString(), 
                round, 
                group: group.name,
                location: finalLocation
            };
            const response = await championshipService.registerMatch(championshipId, matchData);
            
            onMatchAdded(group.name, response.data);

        } catch (error) {
            setError(error.response?.data?.error || 'Erro ao criar partida.');
        } finally {
            setTeamA('');
            setTeamB('');
            setManualLocation('');
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="add-match-form">
            <h4>Adicionar Partida Manual</h4>
            <div className="form-row">
                <select value={teamA} onChange={e => setTeamA(e.target.value)} required>
                    <option value="">Time A</option>
                    {teams.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <span>vs</span>
                <select value={teamB} onChange={e => setTeamB(e.target.value)} required>
                    <option value="">Time B</option>
                    {teams.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
            </div>
            <div className="form-row">
                <input type="date" value={date} onChange={e => setDate(e.target.value)} required />
                <input type="time" value={time} onChange={e => setTime(e.target.value)} required />
                <input type="number" value={round} onChange={e => setRound(e.target.value)} min="1" placeholder="Rodada" required />
            </div>
            <div className="form-row-location">
                <label htmlFor="match-location-option">Local:</label>
                <select id="match-location-option" value={locationOption} onChange={e => setLocationOption(e.target.value)}>
                    <option value="default">Usar local padrão ({championshipVenue || 'Não definido'})</option>
                    <option value="manual">Outro local (digitar)</option>
                </select>
            </div>
            {locationOption === 'manual' && (
                <div className="form-row">
                    <input
                        type="text"
                        value={manualLocation}
                        onChange={e => setManualLocation(e.target.value)}
                        placeholder="Digite o local da partida"
                        required={locationOption === 'manual'}
                    />
                </div>
            )}
            <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Adicionando...' : 'Adicionar'}
            </button>
            {error && <p className="error-message">{error}</p>}
        </form>
    );
};

// Componente para adicionar partidas intergrupos manualmente
const AddIntergroupMatchForm = ({ groups, onMatchAdded, championshipId, championshipVenue }) => {
    const [groupA, setGroupA] = useState('');
    const [groupB, setGroupB] = useState('');
    const [teamA, setTeamA] = useState('');
    const [teamB, setTeamB] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState('15:00');
    const [round, setRound] = useState('1');
    const [locationOption, setLocationOption] = useState('default');
    const [manualLocation, setManualLocation] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => { setTeamA(''); }, [groupA]);
    useEffect(() => { setTeamB(''); }, [groupB]);

    const teamsA = useMemo(() => groups.find(g => g.name === groupA)?.teams || [], [groups, groupA]);
    const teamsB = useMemo(() => groups.find(g => g.name === groupB)?.teams || [], [groups, groupB]);
    const groupsForB = useMemo(() => groups.filter(g => g.name !== groupA), [groups, groupA]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!groupA || !groupB) { setError('Selecione os dois grupos.'); return; }
        if (!teamA || !teamB) { setError('Selecione um time de cada grupo.'); return; }
        if (teamA === teamB) { setError('Os times devem ser diferentes.'); return; }
        const finalLocation = locationOption === 'default' ? (championshipVenue || 'A definir') : manualLocation.trim();
        if (!finalLocation) { setError('Defina o local da partida.'); return; }
        setIsSubmitting(true);
        try {
            const dateTime = new Date(`${date}T${time}`);
            const groupName = `${groupA} vs ${groupB}`;
            const response = await championshipService.registerMatch(championshipId, {
                teamA,
                teamB,
                date: dateTime.toISOString(),
                round: parseInt(round, 10) || 1,
                group: groupName,
                phase: 'intergroup',
                location: finalLocation,
            });
            
            onMatchAdded(groupName, response.data);
            
            setGroupA(''); setGroupB(''); setTeamA(''); setTeamB('');
            setManualLocation(''); setRound('1');
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Erro ao criar partida intergrupos.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="add-match-form intergroup-form">
            <h4>Adicionar Partida Intergrupos</h4>
            <div className="form-row">
                <div className="intergroup-side">
                    <label>Grupo A</label>
                    <select value={groupA} onChange={e => setGroupA(e.target.value)} required>
                        <option value="">Selecione...</option>
                        {groups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                    </select>
                    {groupA && (
                        <>
                            <label>Time do {groupA}</label>
                            <select value={teamA} onChange={e => setTeamA(e.target.value)} required>
                                <option value="">Selecione...</option>
                                {teamsA.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                            </select>
                        </>
                    )}
                </div>
                <span className="vs-separator">vs</span>
                <div className="intergroup-side">
                    <label>Grupo B</label>
                    <select value={groupB} onChange={e => setGroupB(e.target.value)} required>
                        <option value="">Selecione...</option>
                        {groupsForB.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                    </select>
                    {groupB && (
                        <>
                            <label>Time do {groupB}</label>
                            <select value={teamB} onChange={e => setTeamB(e.target.value)} required>
                                <option value="">Selecione...</option>
                                {teamsB.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                            </select>
                        </>
                    )}
                </div>
            </div>
            <div className="form-row">
                <input type="date" value={date} onChange={e => setDate(e.target.value)} required />
                <input type="time" value={time} onChange={e => setTime(e.target.value)} required />
                <input type="number" value={round} onChange={e => setRound(e.target.value)} min="1" placeholder="Rodada" />
            </div>
            <div className="form-row-location">
                <label>Local:</label>
                <select value={locationOption} onChange={e => setLocationOption(e.target.value)}>
                    <option value="default">Usar local padrão ({championshipVenue || 'Não definido'})</option>
                    <option value="manual">Outro local (digitar)</option>
                </select>
            </div>
            {locationOption === 'manual' && (
                <div className="form-row">
                    <input type="text" value={manualLocation} onChange={e => setManualLocation(e.target.value)} placeholder="Digite o local da partida" required />
                </div>
            )}
            {error && <p className="error-message">{error}</p>}
            <button type="submit" disabled={isSubmitting} className="intergroup-submit-btn">
                {isSubmitting ? 'Adicionando...' : 'Adicionar Partida Intergrupos'}
            </button>
        </form>
    );
};

function ManageGroups({ championship }) {
    const { id: championshipId } = useParams();
    const { user } = useAuth();

    const [championshipData, setChampionshipData] = useState(championship);
    const [unassignedTeams, setUnassignedTeams] = useState([]);
    const [groups, setGroups] = useState([]);
    const [matches, setMatches] = useState({});
    const [loading, setLoading] = useState(true);
    const [newGroupName, setNewGroupName] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    
    const [showPairingTool, setShowPairingTool] = useState(false);
    const [phaseToPair, setPhaseToPair] = useState('roundOf16');
    const [movingTeam, setMovingTeam] = useState(null);
    const [targetGroup, setTargetGroup] = useState('');
    const [isMoving, setIsMoving] = useState(false);
    const [currentView, setCurrentView] = useState('overview');
    
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingMatch, setEditingMatch] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [showQualifierSelector, setShowQualifierSelector] = useState(false);
    const [selectedQualifiers, setSelectedQualifiers] = useState([]);
    const [teamsForPairing, setTeamsForPairing] = useState([]);
    const [userSelectedKnockoutSize, setUserSelectedKnockoutSize] = useState(0);

    const [isDistributing, setIsDistributing] = useState(false);
    const [animatingTeams, setAnimatingTeams] = useState({});

    // Estados para gerenciamento de fases do mata-mata
    const [selectedPhase, setSelectedPhase] = useState(null);
    const [showPhaseGenerator, setShowPhaseGenerator] = useState(false);
    const [isGeneratingPhase, setIsGeneratingPhase] = useState(false);
    const [nextPhaseToGenerate, setNextPhaseToGenerate] = useState(null);

    // Estados do Modal em Massa
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkModalTab, setBulkModalTab] = useState('create');
    const [numGroupsToCreate, setNumGroupsToCreate] = useState('1');
    const [bulkSelectedTeams, setBulkSelectedTeams] = useState([]);
    const [bulkTargetGroup, setBulkTargetGroup] = useState('');
    const [isBulkMoving, setIsBulkMoving] = useState(false);

    const canManage = useMemo(() => {
        if (!user || !championshipData) return false;
        if (user.role && user.role.toLowerCase() === 'admin') return true;
        const userId = user._id || user.id;
        if (!userId) return false;
        const isCreator = championshipData.creator?._id?.toString() === userId;
        const isManager = championshipData.managers?.some(m => m._id?.toString() === userId);
        return isCreator || isManager;
    }, [user, championshipData]);

    const knockoutPhasesList = [
        { key: 'roundOf32', title: '32ªs de Final', teamsNeeded: 32 },
        { key: 'roundOf16', title: 'Oitavas de Final', teamsNeeded: 16 },
        { key: 'quarterFinals', title: 'Quartas de Final', teamsNeeded: 8 },
        { key: 'semiFinals', title: 'Semifinal', teamsNeeded: 4 },
        { key: 'final', title: 'Final', teamsNeeded: 2 },
        { key: 'thirdPlace', title: 'Disputa de 3º Lugar', teamsNeeded: 2 },
    ];

    const getAllTeams = useMemo(() => {
        const groupTeams = groups.flatMap(g => g.teams || []);
        return [...groupTeams, ...unassignedTeams];
    }, [groups, unassignedTeams]);

    const isPhaseGenerated = useCallback((phaseMatches) => {
        if (!phaseMatches) return false;
        return Array.isArray(phaseMatches) ? phaseMatches.length > 0 : !!phaseMatches._id;
    }, []);

    const isPhaseCompleted = useCallback((phaseMatches) => {
        if (!phaseMatches) return false;
        if (!Array.isArray(phaseMatches) && phaseMatches._id)
            return phaseMatches.status === 'completed' && phaseMatches.winningTeam;
        return Array.isArray(phaseMatches) && phaseMatches.length > 0 && phaseMatches.every(m => m.status === 'completed');
    }, []);

    const determineNextPhase = useCallback(() => {
        if (!championshipData) return null;
        const format = championshipData.format || '';
        let phasesInOrder = ['roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace'];
        if (format.includes('8 times')) phasesInOrder = ['quarterFinals', 'semiFinals', 'final', 'thirdPlace'];
        if (format.includes('4 times')) phasesInOrder = ['semiFinals', 'final', 'thirdPlace'];
        for (const phaseKey of phasesInOrder) {
            const pm = matches[phaseKey];
            if (!isPhaseGenerated(pm) || !isPhaseCompleted(pm)) return phaseKey;
        }
        return null;
    }, [championshipData, matches, isPhaseGenerated, isPhaseCompleted]);

    useEffect(() => {
        setNextPhaseToGenerate(determineNextPhase());
    }, [determineNextPhase]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [champRes, unassignedRes, groupsRes, matchesRes] = await Promise.all([
                championshipService.getById(championshipId),
                championshipService.getUnassignedTeams(championshipId),
                getGroupPreview(championshipId),
                championshipService.getMatchesByChampionship(championshipId),
            ]);
            
            setChampionshipData(champRes.data);
            setUnassignedTeams(unassignedRes.data || []);
            setGroups(groupsRes.success ? groupsRes.data : []);

            const matchesData = matchesRes.data || [];
            const matchesByGroupAndPhase = matchesData.reduce((acc, match) => {
                if (!match.teamA || !match.teamA.name) {
                    match.teamA = champRes.data.teams.find(t => t._id === match.teamA) || match.teamA;
                }
                if (!match.teamB || !match.teamB.name) {
                    match.teamB = champRes.data.teams.find(t => t._id === match.teamB) || match.teamB;
                }

                if (match.phase === 'intergroup' || (match.group && match.group.includes(' vs '))) {
                    if (!acc['intergroup']) acc['intergroup'] = [];
                    if (!acc['intergroup'].find(m => m._id === match._id)) {
                        acc['intergroup'].push(match);
                    }
                } else if (match.group) {
                    if (!acc[match.group]) acc[match.group] = [];
                    acc[match.group].push(match);
                } else if (match.phase && match.phase !== 'group') {
                    if (!acc[match.phase]) acc[match.phase] = [];
                    acc[match.phase].push(match);
                }
                return acc;
            }, {});
            setMatches(matchesByGroupAndPhase);

        } catch (error) {
            console.error("Erro ao buscar dados", error);
        } finally {
            setLoading(false);
        }
    }, [championshipId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const animateTeamRemoval = (teamId) => {
        setAnimatingTeams(prev => ({ ...prev, [teamId]: 'removing' }));
        return new Promise(resolve => {
            setTimeout(() => {
                setAnimatingTeams(prev => {
                    const newState = { ...prev };
                    delete newState[teamId];
                    return newState;
                });
                resolve();
            }, 300);
        });
    };

    const animateTeamAddition = (teamId) => {
        setAnimatingTeams(prev => ({ ...prev, [teamId]: 'adding' }));
        setTimeout(() => {
            setAnimatingTeams(prev => {
                const newState = { ...prev };
                delete newState[teamId];
                return newState;
            });
        }, 300);
    };

    const handleMatchAdded = useCallback((groupName, newMatch) => {
        if (groupName.includes(' vs ')) {
            setMatches(prev => {
                const updated = { ...prev };
                if (!updated['intergroup']) updated['intergroup'] = [];
                if (!updated['intergroup'].find(m => m._id === newMatch._id)) {
                    updated['intergroup'].push(newMatch);
                }
                return updated;
            });
        } else {
            setMatches(prev => ({
                ...prev,
                [groupName]: [...(prev[groupName] || []), newMatch]
            }));
        }
    }, []);

    const handleEditClick = (match) => {
        const matchDate = new Date(match.date);
        setEditingMatch({
            ...match,
            dateInput: matchDate.toISOString().split('T')[0],
            timeInput: matchDate.toTimeString().substring(0, 5),
            locationInput: match.location || '',
            roundInput: match.round?.toString() || '1'
        });
        setIsEditModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setEditingMatch(null);
    };

    const handleUpdateMatch = async () => {
        if (!editingMatch) return;
        
        const newDateTime = new Date(`${editingMatch.dateInput}T${editingMatch.timeInput}`);
        if (isNaN(newDateTime.getTime())) {
            alert('Data ou horário inválidos. Por favor, verifique os valores.');
            return;
        }
        
        setIsSaving(true);
        try {
            await matchService.update(editingMatch._id, { 
                date: newDateTime.toISOString(), 
                location: editingMatch.locationInput,
                round: parseInt(editingMatch.roundInput, 10) || 1
            });
            handleCloseModal();
            await fetchData();
            alert('Partida atualizada com sucesso!');
        } catch (error) {
            alert('Erro ao atualizar a partida.');
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAllMatches = async () => {
        if (!window.confirm(
            '⚠️ ATENÇÃO: PERIGO!\n\n' +
            'Esta ação apagará PERMANENTEMENTE todas as partidas do campeonato ' +
            '(Grupos e Mata-mata), resetando os scores e classificações.\n\n' +
            'Deseja prosseguir?'
        )) {
            return;
        }
        
        setLoading(true);
        try {
            await matchService.deleteAllMatches(championshipId);
            alert('Todas as partidas foram deletadas com sucesso!');
            await fetchData();
        } catch (error) {
            console.error('Erro ao deletar todas as partidas:', error);
            alert('Não foi possível excluir as partidas do campeonato.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMatch = async (matchId, phaseKey) => {
        if (window.confirm('Tem certeza que deseja excluir esta partida?')) {
            try {
                await matchService.delete(matchId);
                setMatches(prev => ({
                    ...prev,
                    [phaseKey]: prev[phaseKey].filter(m => m._id !== matchId)
                }));
            } catch (error) {
                alert('Erro ao excluir a partida.');
                fetchData();
            }
        }
    };

    const handleOnDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

        const teamId = result.draggableId;
        const fromGroupName = source.droppableId === 'unassigned' ? null : source.droppableId;
        const toGroupName = destination.droppableId === 'unassigned' ? null : destination.droppableId;

        animateTeamRemoval(teamId);

        let movedTeam;
        let currentUnassigned = [...unassignedTeams];
        let currentGroups = JSON.parse(JSON.stringify(groups));

        if (fromGroupName === null) {
            [movedTeam] = currentUnassigned.splice(source.index, 1);
        } else {
            const sourceGroup = currentGroups.find(g => g.name === fromGroupName);
            [movedTeam] = sourceGroup.teams.splice(source.index, 1);
        }

        if (toGroupName === null) {
            currentUnassigned.splice(destination.index, 0, movedTeam);
        } else {
            const destGroup = currentGroups.find(g => g.name === toGroupName);
            destGroup.teams = destGroup.teams || [];
            destGroup.teams.splice(destination.index, 0, movedTeam);
        }

        setUnassignedTeams(currentUnassigned);
        setGroups(currentGroups);
        animateTeamAddition(teamId);

        try {
            await championshipService.moveTeamToGroup(championshipId, { teamId, fromGroupName, toGroupName });
        } catch (error) {
            console.error("Erro ao mover time, revertendo UI", error);
            fetchData();
        } finally {
            setMovingTeam(null);
            setTargetGroup('');
            setIsMoving(false);
        }
    };

    const handleRemoveFromGroup = async (teamId, groupName) => {
        if (!window.confirm('Tem certeza que deseja remover este time do grupo?')) return;

        await animateTeamRemoval(teamId);

        try {
            await championshipService.moveTeamToGroup(championshipId, {
                teamId,
                fromGroupName: groupName,
                toGroupName: null
            });

            setGroups(current => current.map(g => {
                if (g.name === groupName) {
                    return {
                        ...g,
                        teams: (g.teams || []).filter(t => t._id !== teamId)
                    };
                }
                return g;
            }));

            const teamToRemove = groups.find(g => g.name === groupName)
                ?.teams?.find(t => t._id === teamId);
            
            if (teamToRemove) {
                setUnassignedTeams(current => [...current, teamToRemove]);
                animateTeamAddition(teamId);
            }

        } catch (error) {
            console.error("Erro ao remover time do grupo", error);
            alert("Não foi possível remover o time do grupo.");
            fetchData();
        }
    };

    const handleClearGroup = async (groupName) => {
        if (!window.confirm(`Tem certeza que deseja remover todos os times do grupo ${groupName}?`)) return;

        try {
            const group = groups.find(g => g.name === groupName);
            if (!group || !group.teams || group.teams.length === 0) return;

            for (const team of group.teams) {
                await animateTeamRemoval(team._id);
            }

            const promises = group.teams.map(team =>
                championshipService.moveTeamToGroup(championshipId, {
                    teamId: team._id,
                    fromGroupName: groupName,
                    toGroupName: null
                })
            );

            await Promise.all(promises);

            setGroups(current => current.map(g => {
                if (g.name === groupName) {
                    return { ...g, teams: [] };
                }
                return g;
            }));

            setUnassignedTeams(current => [...current, ...group.teams]);
            
            for (const team of group.teams) {
                animateTeamAddition(team._id);
            }

        } catch (error) {
            console.error("Erro ao limpar grupo", error);
            alert("Não foi possível limpar o grupo.");
            fetchData();
        }
    };

    const handleDeleteGroup = async (groupName) => {
        if (!window.confirm(`Tem certeza que deseja excluir o grupo "${groupName}"?\n\nIsso irá:\n• Mover todos os times para "Não Alocados"\n• Excluir todas as partidas pendentes do grupo\n• Remover o grupo permanentemente`)) return;

        try {
            const group = groups.find(g => g.name === groupName);
            if (group?.teams?.length > 0) {
                for (const team of group.teams) {
                    await animateTeamRemoval(team._id);
                }
            }

            await championshipService.deleteGroup(championshipId, groupName);
            
            setGroups(current => current.filter(g => g.name !== groupName));
            
            setMatches(prev => {
                const newMatches = { ...prev };
                delete newMatches[groupName];
                return newMatches;
            });
            
            if (group?.teams?.length > 0) {
                setUnassignedTeams(current => [...current, ...group.teams]);
                for (const team of group.teams) {
                    animateTeamAddition(team._id);
                }
            }
            
            await fetchData();
            alert(`Grupo "${groupName}" excluído com sucesso!`);
            
        } catch (error) {
            console.error("Erro ao excluir grupo:", error);
            alert(error.response?.data?.error || "Não foi possível excluir o grupo.");
            await fetchData();
        }
    };

    const handleMoveTeam = async (teamId, toGroupName) => {
        if (!teamId || !toGroupName || isMoving) return;
        setIsMoving(true);

        const teamToMove = unassignedTeams.find(t => t._id === teamId);
        if (!teamToMove) {
            setIsMoving(false);
            return;
        }

        await animateTeamRemoval(teamId);

        setUnassignedTeams(current => current.filter(t => t._id !== teamId));
        setGroups(current => current.map(g => {
            if (g.name === toGroupName) {
                const updatedTeams = g.teams ? [...g.teams, teamToMove] : [teamToMove];
                return { ...g, teams: updatedTeams };
            }
            return g;
        }));

        animateTeamAddition(teamId);

        try {
            await championshipService.moveTeamToGroup(championshipId, {
                teamId,
                fromGroupName: null,
                toGroupName
            });
        } catch (error) {
            console.error("Erro ao mover time, revertendo UI", error);
            fetchData();
        } finally {
            setMovingTeam(null);
            setTargetGroup('');
            setIsMoving(false);
        }
    };

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        if (!newGroupName.trim()) return;
        try {
            await championshipService.createGroup(championshipId, { groupName: newGroupName });
            setNewGroupName('');
            await fetchData();
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível criar o grupo.");
        }
    };

    const handleGenerateGroups = async () => {
        if (unassignedTeams.length === 0) {
            alert("Não há times não alocados para gerar grupos.");
            return;
        }
        setIsGenerating(true);
        try {
            await championshipService.generateGroups(championshipId);
            await fetchData();
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível gerar os grupos.");
        } finally { 
            setIsGenerating(false); 
        }
    };

    const handleDistributeTeams = async () => {
        if (unassignedTeams.length === 0 || groups.length === 0 || isDistributing) {
            alert(isDistributing ? "Aguarde, distribuição em andamento." : "Crie grupos e tenha times não alocados para distribuir.");
            return;
        }

        if (window.confirm(`Distribuir ${unassignedTeams.length} times igualmente entre ${groups.length} grupos?`)) {

            setIsDistributing(true);

            const teamsToDistribute = [...unassignedTeams].sort(() => Math.random() - 0.5);

            const roundRobinOrder = [];
            const maxRounds = Math.ceil(teamsToDistribute.length / groups.length);
            for (let round = 0; round < maxRounds; round++) {
                for (let g = 0; g < groups.length; g++) {
                    const idx = round * groups.length + g;
                    if (idx < teamsToDistribute.length) {
                        roundRobinOrder.push({ team: teamsToDistribute[idx], groupName: groups[g].name });
                    }
                }
            }

            try {
                for (const { team, groupName } of roundRobinOrder) {
                    await animateTeamRemoval(team._id);

                    setUnassignedTeams(current => current.filter(t => t._id !== team._id));

                    setGroups(current => current.map(g => {
                        if (g.name === groupName) {
                            return { ...g, teams: [...(g.teams || []), team] };
                        }
                        return g;
                    }));

                    animateTeamAddition(team._id);

                    await new Promise(resolve => setTimeout(resolve, 350));

                    await championshipService.moveTeamToGroup(championshipId, {
                        teamId: team._id,
                        fromGroupName: null,
                        toGroupName: groupName,
                    });
                }

                await fetchData();

            } catch (error) {
                console.error("Erro ao distribuir times", error);
                alert("Não foi possível distribuir os times. A página será recarregada.");
                fetchData();
            } finally {
                setIsDistributing(false);
            }
        }
    };

    const handleGenerateGroupMatches = async () => {
        try {
            await championshipService.generateGroupMatches(championshipId);
            await fetchData();
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível gerar as partidas.");
        }
    };

    const handleBulkCreateGroups = async () => {
        const qty = parseInt(numGroupsToCreate);
        if (isNaN(qty) || qty <= 0) {
            alert('Digite um número válido de grupos.');
            return;
        }
        setIsGenerating(true);
        try {
            const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
            const existingRes = await getGroupPreview(championshipId);
            const existingGroups = existingRes.success ? existingRes.data : [];
            const existingNames = new Set(existingGroups.map(g => g.name));
            const currentCount = existingGroups.length;

            let created = 0;
            for (let i = 0; i < qty; i++) {
                const letter = alphabet[currentCount + i] || (currentCount + i + 1).toString();
                const gName = `Grupo ${letter}`;
                if (!existingNames.has(gName)) {
                    await championshipService.createGroup(championshipId, { groupName: gName });
                    existingNames.add(gName);
                    created++;
                }
            }
            alert(`${created} grupo(s) criado(s) com sucesso!`);
            setShowBulkModal(false);
            await fetchData();
        } catch (error) {
            console.error('Erro ao criar grupos em massa:', error);
            alert(error.response?.data?.error || 'Ocorreu um erro ao criar os grupos em massa.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleBulkMoveTeams = async () => {
        if (bulkSelectedTeams.length === 0) { alert('Selecione pelo menos um time.'); return; }
        if (!bulkTargetGroup) { alert('Selecione o grupo de destino.'); return; }
        setIsBulkMoving(true);
        try {
            await Promise.all(
                bulkSelectedTeams.map(teamId =>
                    championshipService.moveTeamToGroup(championshipId, {
                        teamId,
                        fromGroupName: null,
                        toGroupName: bulkTargetGroup,
                    })
                )
            );
            alert(`${bulkSelectedTeams.length} time(s) movido(s) para ${bulkTargetGroup}!`);
            setBulkSelectedTeams([]);
            setBulkTargetGroup('');
            setShowBulkModal(false);
            await fetchData();
        } catch (error) {
            alert('Não foi possível mover os times.');
        } finally {
            setIsBulkMoving(false);
        }
    };

    const handleGenerateIntergroupMatches = async () => {
        try {
            await championshipService.generateIntergroupMatches(championshipId);
            await fetchData();
            alert('Confrontos entre grupos gerados com sucesso!');
        } catch (error) {
            alert(error.response?.data?.error || 'Não foi possível gerar confrontos intergrupos.');
        }
    };

    const handleGenerateKnockoutPhase = async (phaseKey, pairings, scheduleData) => {
        setIsGeneratingPhase(true);
        try {
            if (pairings && pairings.length > 0) {
                const promises = pairings.map(pair =>
                    championshipService.registerMatch(championshipId, {
                        teamA: pair.teamA._id,
                        teamB: pair.teamB._id,
                        date: scheduleData.date,
                        location: scheduleData.location,
                        round: 1,
                        phase: phaseKey,
                    })
                );
                await Promise.all(promises);
            } else {
                await championshipService.generateKnockoutPhase(championshipId, phaseKey);
            }
            setShowPhaseGenerator(false);
            setSelectedPhase(null);
            await fetchData();
        } catch (error) {
            alert(error.response?.data?.error || 'Falha ao gerar a fase.');
        } finally {
            setIsGeneratingPhase(false);
        }
    };

    const handlePairingComplete = () => {
        setShowPairingTool(false);
        fetchData();
    };

    const handleCancelPairing = () => {
        setShowPairingTool(false);
    };
    
    const handleQualifierSelection = (team) => {
        setSelectedQualifiers(prev => {
            const isSelected = prev.some(t => t._id === team._id);
            if (isSelected) {
                return prev.filter(t => t._id !== team._id);
            } else {
                if (prev.length < userSelectedKnockoutSize) {
                    return [...prev, team];
                } else {
                    alert(`Você já selecionou o máximo de ${userSelectedKnockoutSize} times.`);
                    return prev;
                }
            }
        });
    };
    
    const handleProceedToPairing = () => {
        const numberOfQualifiers = userSelectedKnockoutSize;
        let nextPhase = 'final';

        switch (numberOfQualifiers) {
            case 16:
                nextPhase = 'roundOf16';
                break;
            case 8:
                nextPhase = 'quarterFinals';
                break;
            case 4:
                nextPhase = 'semiFinals';
                break;
            case 2:
                nextPhase = 'final';
                break;
            default:
                console.error(`Número inesperado de classificados: ${numberOfQualifiers}. Não é possível determinar a fase.`);
                alert("Por favor, selecione um número válido de classificados (2, 4, 8 ou 16).");
                return;
        }

        setPhaseToPair(nextPhase);
        setTeamsForPairing(selectedQualifiers);
        setShowQualifierSelector(false);
        setCurrentView('knockout');
        setShowPairingTool(true);
    };

    const renderGroupMatchesSection = (group) => {
        const groupMatches = matches[group.name] || [];
        
        return (
            <div key={`matches-${group.name}`} className="matches-phase-container">
                <div className="matches-list">
                    <h4>Partidas do {group.name}</h4>
                    {groupMatches.length > 0 ? (
                        groupMatches
                            .sort((a, b) => new Date(a.date) - new Date(b.date) || (a.round || 0) - (b.round || 0))
                            .map(match => (
                                <div key={match._id} className="match-item">
                                    <div className="match-details">
                                        {match.round && <span className="match-round">R{match.round}</span>}
                                        <span className="match-teams">
                                            <span className="team-a">{match.teamA?.name || 'A definir'}</span>
                                            <span className="vs-separator">vs</span>
                                            <span className="team-b">{match.teamB?.name || 'A definir'}</span>
                                        </span>
                                        <span className="match-datetime">
                                            {formatDateTime(match.date)}
                                            {match.location && ` (${match.location})`}
                                        </span>
                                    </div>
                                    {canManage && (
                                        <div className="match-actions">
                                            <button onClick={() => handleEditClick(match)} className="edit-btn">Editar</button>
                                            <button onClick={() => handleDeleteMatch(match._id, group.name)} className="delete-btn">Excluir</button>
                                        </div>
                                    )}
                                </div>
                            ))
                    ) : <p className="no-matches-info">Nenhuma partida agendada.</p>}
                </div>
                
                {canManage && (
                    <AddMatchForm 
                        group={{ name: group.name }} 
                        teams={group.teams || []} 
                        onMatchAdded={handleMatchAdded}
                        championshipVenue={championshipData?.venue || championshipData?.location || 'A definir'}
                    />
                )}
            </div>
        );
    };

    const renderIntergroupMatchesSection = () => {
        const intergroupMatches = matches['intergroup'] || [];
        
        if (intergroupMatches.length === 0) return null;
        
        return (
            <div className="matches-phase-container">
                <div className="matches-list">
                    <h4>⚡ Partidas Intergrupos</h4>
                    {intergroupMatches
                        .sort((a, b) => new Date(a.date) - new Date(b.date) || (a.round || 0) - (b.round || 0))
                        .map(match => (
                            <div key={match._id} className="match-item">
                                <div className="match-details">
                                    {match.round && <span className="match-round">R{match.round}</span>}
                                    <span className="match-teams">
                                        <span className="team-a">{match.teamA?.name || 'A definir'}</span>
                                        <span className="vs-separator">vs</span>
                                        <span className="team-b">{match.teamB?.name || 'A definir'}</span>
                                    </span>
                                    <span className="match-intergroup-badge">Intergrupos</span>
                                    <span className="match-datetime">
                                        {formatDateTime(match.date)}
                                        {match.location && ` (${match.location})`}
                                    </span>
                                </div>
                                {canManage && (
                                    <div className="match-actions">
                                        <button onClick={() => handleEditClick(match)} className="edit-btn">Editar</button>
                                        <button onClick={() => handleDeleteMatch(match._id, 'intergroup')} className="delete-btn">Excluir</button>
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            </div>
        );
    };

    const renderPhaseSection = (phaseKey, title, teamsInvolved = [], isGroupPhase = false) => {
        const phaseMatches = matches[phaseKey] || [];
        const championshipVenue = championshipData?.venue || championshipData?.location || 'A definir';

        return (
            <div className="matches-section" key={phaseKey}>
                <div className="matches-list">
                    <h4>{title}</h4>
                    {phaseMatches.length > 0 ? (
                        phaseMatches
                            .sort((a, b) => new Date(a.date) - new Date(b.date) || (a.round || 0) - (b.round || 0))
                            .map(match => (
                                <div key={match._id} className="match-item">
                                    <div className="match-details">
                                        {match.round && <span className="match-round">R{match.round}</span>}
                                        <span className="match-teams">
                                            <span className="team-a">{match.teamA?.name || 'A definir'}</span>
                                            <span className="vs-separator">vs</span>
                                            <span className="team-b">{match.teamB?.name || 'A definir'}</span>
                                        </span>
                                        <span className="match-datetime">
                                            {formatDateTime(match.date)}
                                            {match.location && ` (${match.location})`}
                                        </span>
                                    </div>
                                    {canManage && (
                                        <div className="match-actions">
                                            <button onClick={() => handleEditClick(match)} className="edit-btn">Editar</button>
                                            <button onClick={() => handleDeleteMatch(match._id, phaseKey)} className="delete-btn">Excluir</button>
                                        </div>
                                    )}
                                </div>
                            ))
                    ) : <p className="no-matches-info">Nenhuma partida agendada.</p>}
                </div>
                
                {isGroupPhase && teamsInvolved && teamsInvolved.length >= 2 && canManage && (
                    <AddMatchForm 
                        group={{ name: phaseKey }} 
                        teams={teamsInvolved} 
                        onMatchAdded={handleMatchAdded}
                        championshipVenue={championshipVenue}
                    />
                )}
            </div>
        );
    };

    const ViewSelector = () => (
        <div className="view-selector">
            <button onClick={() => setCurrentView('overview')} className={currentView === 'overview' ? 'active' : ''}>Visão Geral</button>
            <button onClick={() => setCurrentView('groups')} className={currentView === 'groups' ? 'active' : ''}>Gerenciar Grupos</button>
            <button onClick={() => setCurrentView('matches')} className={currentView === 'matches' ? 'active' : ''}>Gerenciar Partidas</button>
            <button onClick={() => setCurrentView('knockout')} className={currentView === 'knockout' ? 'active' : ''}>Gerenciar Mata-Mata</button>
        </div>
    );

    const renderQualifierSelector = () => {
        const totalTeamsInGroups = groups.flatMap(group => group.teams || []).length;
        const validKnockoutSizes = [16, 8, 4, 2].filter(size => size <= totalTeamsInGroups);

        return (
            <div className="qualifier-selector-view">
                <h4>Selecione Manualmente os Times Classificados</h4>
                <p>Escolha o tamanho do mata-mata e então os times que avançarão.</p>
                
                <div className="qualifier-selector-controls">
                    <label htmlFor="selectKnockoutSize">Número de Classificados:</label>
                    <select
                        id="selectKnockoutSize"
                        value={userSelectedKnockoutSize}
                        onChange={(e) => {
                            const size = parseInt(e.target.value, 10);
                            setUserSelectedKnockoutSize(size);
                            setSelectedQualifiers([]);
                        }}
                    >
                        <option value="0">-- Selecione o tamanho --</option>
                        {validKnockoutSizes.map(size => (
                            <option key={size} value={size}>{size} Times</option>
                        ))}
                    </select>
                </div>
                
                {userSelectedKnockoutSize > 0 && (
                    <p>Você deve selecionar <strong>{userSelectedKnockoutSize}</strong> times.</p>
                )}

                <div className="groups-for-selection">
                    {groups.map(group => (
                        <div key={group.name} className="group-selection-card">
                            <h5>{group.name}</h5>
                            <ul>
                                {(group.teams || []).map(team => (
                                    <li key={team._id}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={selectedQualifiers.some(t => t._id === team._id)}
                                                onChange={() => handleQualifierSelection(team)}
                                                disabled={!userSelectedKnockoutSize || (selectedQualifiers.length >= userSelectedKnockoutSize && !selectedQualifiers.some(t => t._id === team._id))}
                                            />
                                            {team.name}
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="qualifier-selector-footer">
                    <p><strong>{selectedQualifiers.length} de {userSelectedKnockoutSize || '0'} times selecionados.</strong></p>
                    <button
                        className="generate-matches-btn"
                        disabled={selectedQualifiers.length !== userSelectedKnockoutSize || userSelectedKnockoutSize === 0}
                        onClick={handleProceedToPairing}
                    >
                        Prosseguir para Emparelhamento
                    </button>
                    <button
                        className="cancel-btn"
                        onClick={() => setShowQualifierSelector(false)}
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        );
    };

    const renderCurrentView = () => {
        if (showQualifierSelector) {
            return renderQualifierSelector();
        }
        switch (currentView) {
            case 'groups':
                return renderGroupManagementView();
            case 'matches':
                return renderMatchManagementView();
            case 'knockout':
                return renderKnockoutManagementView();
            case 'overview':
            default:
                return renderOverview();
        }
    };
    
    const renderOverview = () => (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <div className="panels-wrapper">
                {renderUnassignedPanel()}
                <div className="groups-panel horizontal-layout">
                    {groups.length > 0 ? groups.map(group => (
                        <div key={group.name} className="group-container horizontal">
                            {renderGroupColumn(group)}
                        </div>
                    )) : <p>Nenhum grupo criado.</p>}
                </div>
            </div>
        </DragDropContext>
    );

    const renderGroupManagementView = () => (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <div className="panels-wrapper">
                {renderUnassignedPanel()}
                <div className="groups-panel horizontal-layout">
                    {groups.length > 0 ? groups.map(group => (
                        <div key={group.name} className="group-container horizontal">
                            {renderGroupColumn(group)}
                        </div>
                    )) : <p>Nenhum grupo criado. Use os controles acima para começar.</p>}
                </div>
            </div>
        </DragDropContext>
    );
    
    const renderMatchManagementView = () => {
        const knockoutPhaseOrder = ['roundOf32', 'roundOf16', 'quarterFinals', 'semiFinals', 'thirdPlace', 'final'];
        const hasIntergroupMatches = matches['intergroup'] && matches['intergroup'].length > 0;
        
        const hasGroupMatches = groups.some(g => matches[g.name] && matches[g.name].length > 0);
        const hasKnockoutMatches = knockoutPhaseOrder.some(key => matches[key] && matches[key].length > 0);
        
        const hasAnyMatches = hasGroupMatches || hasIntergroupMatches || hasKnockoutMatches;

        return (
            <div className="panels-wrapper">
                <div className="groups-panel full-width">
                    <h3>Gerenciar Partidas</h3>
                    {canManage && (
                        <button 
                            onClick={handleDeleteAllMatches} 
                            className="delete-btn"
                            style={{ 
                                background: '#dc3545', 
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '10px 20px',
                                marginBottom: '15px',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'inline-block'
                            }}
                        >
                            🗑️ Excluir Todas as Partidas
                        </button>
                    )}
                    {!hasAnyMatches ? (
                        <p>Nenhuma partida encontrada. Crie grupos e gere partidas ou use a ferramenta de mata-mata.</p>
                    ) : (
                        <div className="matches-grid-container">
                            {groups.map(group => renderGroupMatchesSection(group))}
                            
                            {renderIntergroupMatchesSection()}
                            
                            {canManage && championshipData?.intergroupStage && groups.length >= 2 && (
                                <div className="matches-phase-container">
                                    <AddIntergroupMatchForm
                                        groups={groups}
                                        onMatchAdded={handleMatchAdded}
                                        championshipId={championshipId}
                                        championshipVenue={championshipData?.venue || championshipData?.location || 'A definir'}
                                    />
                                </div>
                            )}
                            
                            {knockoutPhaseOrder.map(phaseKey => {
                                if (matches[phaseKey] && matches[phaseKey].length > 0) {
                                    const phaseTitle = translatePhaseToPortuguese(phaseKey);
                                    return (
                                        <div key={`matches-${phaseKey}`} className="matches-phase-container">
                                            {renderPhaseSection(phaseKey, `Partidas da ${phaseTitle}`)}
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderKnockoutManagementView = () => (
        <div className="knockout-management-view">
            <div className="knockout-card">
                <h4 className="knockout-title">⚡ Gerar Fases do Mata-Mata ⚡</h4>
                <p className="knockout-description">
                    Selecione uma fase para gerar os confrontos manualmente ou use a ferramenta automática.
                </p>

                <div className="phases-grid">
                    {knockoutPhasesList
                        .filter(phase => {
                            const hasMatches = matches[phase.key] && matches[phase.key].length > 0;
                            return hasMatches || phase.teamsNeeded <= getAllTeams.length;
                        })
                        .map(phase => {
                            const hasMatches = matches[phase.key] && matches[phase.key].length > 0;
                            const isNextPhase = nextPhaseToGenerate === phase.key;
                            return (
                                <div
                                    key={phase.key}
                                    className={`phase-card${hasMatches ? ' phase-card-completed' : ''}${isNextPhase ? ' phase-card-next' : ''}`}
                                    onClick={() => { setSelectedPhase(phase); setShowPhaseGenerator(true); }}
                                >
                                    <span className="phase-card-title">{phase.title}</span>
                                    <span className="phase-card-subtitle">
                                        {hasMatches
                                            ? `${matches[phase.key].length} partida(s) gerada(s)`
                                            : `${phase.teamsNeeded} times necessários`}
                                    </span>
                                    {hasMatches && <span className="phase-card-badge">GERADA</span>}
                                    {isNextPhase && !hasMatches && <span className="phase-card-badge phase-card-next-badge">PRÓXIMA</span>}
                                </div>
                            );
                        })}
                </div>

                {knockoutPhasesList.map(phase => {
                    const phaseMatches = matches[phase.key];
                    if (!phaseMatches || phaseMatches.length === 0) return null;
                    return (
                        <div key={`km-${phase.key}`} className="matches-phase-container">
                            {renderPhaseSection(phase.key, `Partidas — ${phase.title}`)}
                        </div>
                    );
                })}

                <div className="knockout-controls" style={{ marginTop: '20px' }}>
                    <button
                        className="generate-matches-btn"
                        onClick={() => { setTeamsForPairing([]); setShowPairingTool(true); }}
                    >
                        🛠 Abrir Ferramenta Avançada (Automático)
                    </button>
                    <button
                        className="generate-groups-btn"
                        onClick={() => {
                            setSelectedQualifiers([]);
                            const allCount = getAllTeams.length;
                            const defaultSize = allCount >= 16 ? 16 : allCount >= 8 ? 8 : allCount >= 4 ? 4 : allCount >= 2 ? 2 : 0;
                            setUserSelectedKnockoutSize(defaultSize);
                            setShowQualifierSelector(true);
                        }}
                    >
                        ✋ Selecionar Classificados Manualmente
                    </button>
                    {canManage && (
                        <button
                            onClick={handleDeleteAllMatches}
                            className="delete-btn"
                            style={{ 
                                background: '#dc3545', 
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            🗑️ Excluir Todas as Partidas
                        </button>
                    )}
                </div>

                {!nextPhaseToGenerate && (
                    <p style={{ color: '#28a745', textAlign: 'center', marginTop: '10px', fontSize: '13px' }}>
                        Todas as fases já foram geradas ou estão completas!
                    </p>
                )}
            </div>

            {showPhaseGenerator && selectedPhase && (
                <div className="phase-generator-modal-overlay">
                    <div className="phase-generator-modal">
                        <div className="phase-generator-header">
                            <h4>Gerar {selectedPhase.title}</h4>
                            <button className="cancel-btn" onClick={() => { setShowPhaseGenerator(false); setSelectedPhase(null); }}>✕</button>
                        </div>
                        {/* ✅ COMPONENTE CORRIGIDO COM targetPhase */}
                        <KnockoutPhaseGeneratorWeb
                            phase={selectedPhase.key}
                            phaseTitle={selectedPhase.title}
                            championshipId={championshipId}
                            onGenerate={handleGenerateKnockoutPhase}
                            isGenerating={isGeneratingPhase}
                            existingMatches={matches[selectedPhase.key]}
                            defaultLocation={championshipData?.venue || championshipData?.location || ''}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    const renderUnassignedPanel = () => (
        <Droppable droppableId="unassigned">
            {(provided) => (
                <div 
                    className={`team-panel unassigned-panel ${isDistributing ? 'is-distributing' : ''}`} 
                    {...provided.droppableProps} 
                    ref={provided.innerRef}
                >
                    <div className="panel-header">
                        <h3>Times não Alocados ({unassignedTeams.length})</h3>
                        {canManage && unassignedTeams.length > 0 && (
                            <button 
                                onClick={handleDistributeTeams}
                                className="move-btn"
                                style={{ fontSize: '0.8rem' }}
                                disabled={isDistributing}
                            >
                                {isDistributing ? 'Distribuindo...' : 'Distribuir'}
                            </button>
                        )}
                    </div>
                    {unassignedTeams.map((team, index) => (
                        <Draggable key={team._id} draggableId={team._id.toString()} index={index}>
                            {(provided) => (
                                <div 
                                    className={`team-card-wrapper ${isDistributing ? 'distributing' : ''} ${animatingTeams[team._id] || ''}`}
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                    ref={provided.innerRef} 
                                    {...provided.draggableProps}
                                >
                                    <div className="team-card" {...provided.dragHandleProps}>
                                        <div className="team-info">
                                            {team.badge && <img src={team.badge} alt={team.name} />}
                                            <span>{team.name}</span>
                                        </div>
                                        {canManage && groups.length > 0 && (
                                            <div className="team-actions">
                                                <button 
                                                    onClick={() => { 
                                                        setMovingTeam(team); 
                                                        setTargetGroup(groups[0]?.name || ''); 
                                                    }} 
                                                    className="move-btn"
                                                >
                                                    ADICIONAR
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {movingTeam?._id === team._id && (
                                        <div className="move-team-controls">
                                            <select value={targetGroup} onChange={e => setTargetGroup(e.target.value)}>
                                                {groups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                            </select>
                                            <button onClick={() => handleMoveTeam(movingTeam._id, targetGroup)} disabled={isMoving}>
                                                {isMoving ? '...' : 'OK'}
                                            </button>
                                            <button onClick={() => setMovingTeam(null)} className="cancel-btn">X</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Draggable>
                    ))}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );

    const renderGroupColumn = (group) => (
        <Droppable droppableId={group.name}>
            {(provided) => (
                <div className="group-column-wrapper">
                    <div className="group-header">
                        <h3>{group.name} ({(group.teams || []).length})</h3>
                        {canManage && (
                            <div className="group-actions">
                                <button 
                                    onClick={() => handleClearGroup(group.name)}
                                    className="clear-group-btn"
                                    title="Remover todos os times do grupo"
                                >
                                    Limpar
                                </button>
                                <button 
                                    onClick={() => handleDeleteGroup(group.name)}
                                    className="delete-group-btn"
                                    title="Excluir grupo permanentemente"
                                >
                                    Excluir
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="group-column" {...provided.droppableProps} ref={provided.innerRef}>
                        {(group.teams || []).map((team, index) => (
                            <Draggable key={team._id} draggableId={team._id.toString()} index={index}>
                                {(provided) => (
                                    <div 
                                        className={`team-card-wrapper ${animatingTeams[team._id] || ''}`}
                                        ref={provided.innerRef} 
                                        {...provided.draggableProps} 
                                        {...provided.dragHandleProps}
                                    >
                                        <div className="team-card">
                                            <div className="team-info">
                                                {team.badge && <img src={team.badge} alt={team.name} />}
                                                <span>{team.name}</span>
                                            </div>
                                            {canManage && (
                                                <div className="team-actions">
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveFromGroup(team._id, group.name);
                                                        }} 
                                                        className="remove-team-btn"
                                                        title="Remover do grupo"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        <div className="dnd-placeholder">{provided.placeholder}</div>
                    </div>
                </div>
            )}
        </Droppable>
    );
    
    if (loading) return <p>Carregando gerenciador de grupos e partidas...</p>;
    
    if (showPairingTool) {
        return (
            <KnockoutPairingTool
                championshipId={championshipId}
                canManage={canManage}
                currentPhase={phaseToPair}
                onPairingComplete={handlePairingComplete}
                onCancel={handleCancelPairing}
                initialTeams={teamsForPairing}
            />
        );
    }

    return (
        <div className="manage-groups-container">
            <div className="page-header">
                <h2>Gerenciar Campeonato</h2>
                <ViewSelector />

                {canManage && (
                    <div className="header-actions">
                        {(currentView === 'overview' || currentView === 'groups') && (
                            <>
                                <form onSubmit={handleCreateGroup} className="create-group-form">
                                    <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Nome do Grupo"/>
                                    <button type="submit">Criar Grupo</button>
                                </form>
                                <button onClick={() => setShowBulkModal(true)} className="bulk-create-btn">
                                    + Criar Vários
                                </button>
                                <button onClick={handleGenerateGroups} className="generate-groups-btn" disabled={isGenerating || groups.length > 0 || unassignedTeams.length === 0}>
                                    {isGenerating ? 'Gerando...' : 'Gerar Grupos (Auto)'}
                                </button>
                                <button onClick={handleDistributeTeams} className="generate-groups-btn" disabled={unassignedTeams.length === 0 || isDistributing}>
                                    {isDistributing ? 'Distribuindo...' : 'Distribuir Times Igualmente'}
                                </button>
                            </>
                        )}
                        {(currentView === 'overview' || currentView === 'matches') && (
                            <>
                                <button onClick={handleGenerateGroupMatches} className="generate-matches-btn" disabled={!groups.length || groups.some(g => (g.teams || []).length < 2)}>
                                    Gerar Todas as Partidas de Grupos
                                </button>
                                <button 
                                    onClick={handleDeleteAllMatches} 
                                    className="delete-btn"
                                    style={{ 
                                        background: '#dc3545', 
                                        color: 'white',
                                        fontWeight: 'bold',
                                        padding: '10px 20px',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🗑️ Excluir Todas as Partidas
                                </button>
                            </>
                        )}
                        {(currentView === 'overview' || currentView === 'matches') && groups.length >= 2 && championshipData?.intergroupStage && (
                            <button onClick={handleGenerateIntergroupMatches} className="generate-matches-btn intergroup-btn">
                                Gerar Confrontos Intergrupos
                            </button>
                        )}
                    </div>
                )}
            </div>
            {renderCurrentView()}

            {/* Modal em Massa: Criar Grupos / Mover Times */}
            {showBulkModal && (
                <div className="edit-match-modal-overlay" onClick={() => { setShowBulkModal(false); setBulkSelectedTeams([]); setBulkTargetGroup(''); }}>
                    <div className="edit-match-modal-content bulk-modal" onClick={e => e.stopPropagation()}>
                        <div className="bulk-modal-tabs">
                            <button
                                className={`bulk-tab-btn${bulkModalTab === 'create' ? ' active' : ''}`}
                                onClick={() => setBulkModalTab('create')}
                            >
                                📁 Criar Grupos
                            </button>
                            <button
                                className={`bulk-tab-btn${bulkModalTab === 'move' ? ' active' : ''}`}
                                onClick={() => setBulkModalTab('move')}
                            >
                                🔀 Mover Times
                            </button>
                        </div>

                        {bulkModalTab === 'create' && (
                            <>
                                <h4>Criar Vários Grupos</h4>
                                <p className="bulk-modal-hint">Quantos grupos criar em sequência alfabética?</p>
                                <input
                                    type="number"
                                    className="bulk-qty-input"
                                    value={numGroupsToCreate}
                                    onChange={e => setNumGroupsToCreate(e.target.value)}
                                    min="1"
                                    max="26"
                                />
                                <div className="modal-actions">
                                    <button onClick={() => setShowBulkModal(false)} className="cancel-btn">Cancelar</button>
                                    <button onClick={handleBulkCreateGroups} disabled={isGenerating} className="save-btn">
                                        {isGenerating ? 'Criando...' : 'Criar Agora'}
                                    </button>
                                </div>
                            </>
                        )}

                        {bulkModalTab === 'move' && (
                            <>
                                <h4>Mover Times para Grupo</h4>
                                <p className="bulk-modal-hint">Times sem grupo ({unassignedTeams.length})</p>
                                {unassignedTeams.length === 0 ? (
                                    <p className="no-matches-info">Nenhum time disponível.</p>
                                ) : (
                                    <div className="bulk-teams-list">
                                        {unassignedTeams.map(team => {
                                            const selected = bulkSelectedTeams.includes(team._id);
                                            return (
                                                <div
                                                    key={team._id}
                                                    className={`bulk-team-row${selected ? ' selected' : ''}`}
                                                    onClick={() => setBulkSelectedTeams(prev =>
                                                        prev.includes(team._id)
                                                            ? prev.filter(id => id !== team._id)
                                                            : [...prev, team._id]
                                                    )}
                                                >
                                                    {team.badge && <img src={team.badge} alt={team.name} className="bulk-team-badge" />}
                                                    <span>{team.name}</span>
                                                    {selected && <span className="bulk-check">✓</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <p className="bulk-modal-hint" style={{ marginTop: 12 }}>Grupo de destino</p>
                                {groups.length === 0 ? (
                                    <p className="no-matches-info">Nenhum grupo criado.</p>
                                ) : (
                                    <div className="bulk-groups-row">
                                        {groups.map(g => (
                                            <button
                                                key={g.name}
                                                className={`bulk-group-option${bulkTargetGroup === g.name ? ' selected' : ''}`}
                                                onClick={() => setBulkTargetGroup(g.name)}
                                            >
                                                {g.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {bulkSelectedTeams.length > 0 && bulkTargetGroup && (
                                    <p className="bulk-modal-hint">{bulkSelectedTeams.length} time(s) → {bulkTargetGroup}</p>
                                )}
                                <div className="modal-actions">
                                    <button onClick={() => { setBulkSelectedTeams([]); setBulkTargetGroup(''); setShowBulkModal(false); }} className="cancel-btn">Cancelar</button>
                                    <button
                                        onClick={handleBulkMoveTeams}
                                        disabled={isBulkMoving || !bulkTargetGroup || bulkSelectedTeams.length === 0}
                                        className="save-btn"
                                    >
                                        {isBulkMoving ? 'Movendo...' : 'Mover Agora'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de Edição com campo de rodada */}
            {isEditModalOpen && editingMatch && (
                <div className="edit-match-modal-overlay">
                    <div className="edit-match-modal-content">
                        <h4>Editar Partida</h4>
                        <p>{editingMatch.teamA?.name || 'A definir'} vs {editingMatch.teamB?.name || 'A definir'}</p>
                        <div className="form-row">
                            <label htmlFor="edit-match-date">Data:</label>
                            <input id="edit-match-date"
                                type="date" 
                                value={editingMatch.dateInput}
                                onChange={(e) => setEditingMatch(prev => ({ ...prev, dateInput: e.target.value }))}
                            />
                        </div>
                        <div className="form-row">
                            <label htmlFor="edit-match-time">Horário:</label>
                            <input id="edit-match-time"
                                type="time" 
                                value={editingMatch.timeInput}
                                onChange={(e) => setEditingMatch(prev => ({ ...prev, timeInput: e.target.value }))}
                            />
                        </div>
                        <div className="form-row">
                            <label htmlFor="edit-match-round">Rodada:</label>
                            <input id="edit-match-round"
                                type="number" 
                                min="1"
                                value={editingMatch.roundInput || '1'}
                                onChange={(e) => setEditingMatch(prev => ({ 
                                    ...prev, 
                                    roundInput: e.target.value 
                                }))} 
                                placeholder="Número da rodada"
                            />
                        </div>
                        <div className="form-row">
                            <label htmlFor="edit-match-location">Local:</label>
                            <input id="edit-match-location" type="text" value={editingMatch.locationInput}
                                onChange={(e) => setEditingMatch(prev => ({ ...prev, locationInput: e.target.value }))} placeholder="Local da partida" />
                        </div>
                        <div className="modal-actions">
                            <button onClick={handleCloseModal} className="cancel-btn">Cancelar</button>
                            <button onClick={handleUpdateMatch} disabled={isSaving} className="save-btn">
                                {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManageGroups;